import moment from "moment/moment";
import React, { useEffect, useMemo } from "react";
import { HStack, VStack } from "native-base";
import { Segmented } from "antd";
import FormInput from "../../../../shared/components/common/FormInput";
import { t } from "../../../../shared/services/i18n";
import AdvanceSelect from "../../../../shared/components/common/AdvanceSelect";
import SharedTimepicker from "../../../../shared/components/common/SharedTimepicker";
import { useDispatch, useSelector } from "react-redux";
import { useToast } from "react-native-toast-notifications";
import { setError } from "../../../../shared/redux/stepperSlice";
import { getLocationsByRivhitId, getLocationsByRivhitIdOptions } from "../../../../shared/redux/boxSlice";
import { checkRequired } from "../../../../shared/services/stepperService";
import { dbDateFormat } from "../../../../shared/services/Helpers";
import { GlobalStyleAttributes } from "../../../../shared/styles/GlobalStyles";

export const UNIFORM_REPORT_TYPE_TAX_YEAR = 'tax_year';
export const UNIFORM_REPORT_TYPE_DATE_RANGE = 'date_range';

const BuildUniformReport = (props) => {
    const { setFieldValue, values, touched, errors, validationObject, handleChange } = props
    const error = useSelector((state) => state.stepper.error);
    const box = useSelector((state) => state.box.box);
    const toast = useToast()
    const dispatch = useDispatch()

    const locationOptions = useMemo(
        () => getLocationsByRivhitIdOptions(box?.locationsByRivhitId),
        [box?.locationsByRivhitId],
    );
    const hasInvoiceSeparation = locationOptions.length > 1;

    const years = useMemo(() => {
        return Array.from({ length: 5 }, (_, i) => moment().subtract(i, 'year').format('YYYY'))
    }, [])

    const reportTypeOptions = useMemo(() => [
        { label: t('tax-year'), value: UNIFORM_REPORT_TYPE_TAX_YEAR },
        { label: t('customDate'), value: UNIFORM_REPORT_TYPE_DATE_RANGE },
    ], []);

    const onReportTypeChange = (reportType) => {
        setFieldValue('report_type', reportType);
        if (reportType === UNIFORM_REPORT_TYPE_TAX_YEAR) {
            setFieldValue('from_date', null);
            setFieldValue('to_date', null);
        } else {
            setFieldValue('tax_year', null);
        }
    };

    useEffect(() => {
        if (box && !box.locationsByRivhitId) {
            dispatch(getLocationsByRivhitId());
        }
    }, [box, dispatch]);

    useEffect(() => {
        if(error && error !== '') {
            toast.show(t(error?.message), { type: 'danger', duration: 5000 });
            dispatch(setError(null))
        }
    }, [error]);

    return (
        <VStack space={'1.5rem'}>
            {hasInvoiceSeparation && (
                <FormInput
                    label={t('location') + checkRequired('location_box', validationObject)}
                    name={'location_box'}
                    error={touched.location_box && errors.location_box}
                    containerProps={{ mb: 0 }}
                    width={'100%'}
                >
                    <AdvanceSelect
                        dontSort
                        options={locationOptions}
                        initValue={values.location_box}
                        onChange={(option) => setFieldValue('location_box', option?.value ?? null)}
                        isError={touched.location_box && errors.location_box}
                    />
                </FormInput>
            )}
            <Segmented
                block
                options={reportTypeOptions}
                value={values.report_type}
                onChange={onReportTypeChange}
            />
            {values.report_type === UNIFORM_REPORT_TYPE_TAX_YEAR ? (
                <FormInput
                    label={t('tax-year') + checkRequired('tax_year', validationObject)}
                    name={'tax_year'}
                    onChangeCallback={handleChange('tax_year')}
                    error={touched.tax_year && errors.tax_year}
                    containerProps={{ mb: 0 }}
                    width={'100%'}
                >
                    <AdvanceSelect
                        options={years.map(year => ({ value: year, label: year }))}
                        initValue={values.tax_year}
                        onChange={(option) => setFieldValue('tax_year', option?.value ?? null)}
                        isError={touched.tax_year && errors.tax_year}
                    />
                </FormInput>
            ) : (
                <HStack space="1rem" alignItems="flex-start" flexWrap="wrap" width="100%">
                    <FormInput
                        label={t('fromDate') + checkRequired('from_date', validationObject)}
                        width="100%"
                        containerProps={{ flex: 1, minWidth: '140px', mb: 0 }}
                        error={touched.from_date && errors.from_date ? errors.from_date : null}
                    >
                        <SharedTimepicker
                            type="date"
                            value={values.from_date ? moment(values.from_date, dbDateFormat) : null}
                            onChange={(date) => setFieldValue('from_date', date ? date.format(dbDateFormat) : null)}
                            textStyle={{ fontSize: GlobalStyleAttributes.fonts.standard16 }}
                            isError={touched.from_date && errors.from_date}
                        />
                    </FormInput>
                    <FormInput
                        label={t('toDate') + checkRequired('to_date', validationObject)}
                        width="100%"
                        containerProps={{ flex: 1, minWidth: '140px', mb: 0 }}
                        error={touched.to_date && errors.to_date ? errors.to_date : null}
                    >
                        <SharedTimepicker
                            type="date"
                            value={values.to_date ? moment(values.to_date, dbDateFormat) : null}
                            onChange={(date) => setFieldValue('to_date', date ? date.format(dbDateFormat) : null)}
                            textStyle={{ fontSize: GlobalStyleAttributes.fonts.standard16 }}
                            isError={touched.to_date && errors.to_date}
                        />
                    </FormInput>
                </HStack>
            )}
        </VStack>
    )
}

export default BuildUniformReport
